/* fourpaw-standard: lifecycle-delegated */
(() => {
  if (window.SafeWalkV6RealGiftLifecycle) return;

  const nativeFetch = window.fetch.bind(window);
  const GROUP = '_safewalk_package_group';
  const ROLE = '_safewalk_package_role';
  const REAL = '_safewalk_real_gift';
  const COMPANION = '_safewalk_companion_gift';
  const GIFT_ROLES = ['gift_tags', 'gift_dispenser', 'gift_roll'];
  const updateUrl = window.routes?.cart_update_url || '/cart/update.js';
  const cartUrl = `${window.routes?.cart_url || '/cart'}.js`.replace(/\.js\.js$/, '.js');
  let queue = Promise.resolve();
  let pendingMutations = 0;
  let deferredState = 'ready';
  const serialize = (task) => {
    const next = queue.then(task, task);
    queue = next.catch(() => {});
    return next;
  };
  const publishState = (state) => {
    document.documentElement.dataset.safewalkGiftReconcile = state;
    document.dispatchEvent(new CustomEvent('safewalk:real-gifts-state', { detail: { state } }));
  };
  const setState = (state) => {
    if (state !== 'pending') deferredState = state;
    if (pendingMutations > 0 && state !== 'pending') return;
    publishState(state);
  };
  const propertiesFor = (item) => item?.properties && typeof item.properties === 'object'
    ? item.properties
    : {};
  const requestUrl = (input) => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    return input && typeof input === 'object' && 'url' in input ? input.url : '';
  };
  const requestPath = (input) => {
    try {
      return new URL(requestUrl(input), window.location.origin).pathname;
    } catch (error) {
      return '';
    }
  };
  const isCartMutation = (input) => /\/cart\/(?:change|update|clear)(?:\.js)?\/?$/
    .test(requestPath(input));
  const parseJson = (value) => {
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  };
  const requestPayload = async (input, init = {}) => {
    if (typeof init.body === 'string') return parseJson(init.body);
    if (!(input instanceof Request)) return null;
    try {
      return parseJson(await input.clone().text());
    } catch (error) {
      return null;
    }
  };
  const responseCart = async (response) => {
    try {
      const body = await response.clone().json();
      return Array.isArray(body?.items) ? body : null;
    } catch (error) {
      return null;
    }
  };
  const mainGroups = (cart) => new Set((cart?.items || [])
    .filter((item) => propertiesFor(item)[ROLE] === 'main')
    .map((item) => String(propertiesFor(item)[GROUP] || ''))
    .filter(Boolean));
  const cleanupUpdates = (cart) => {
    const mains = mainGroups(cart);
    const updates = {};
    (cart?.items || []).forEach((item) => {
      const properties = propertiesFor(item);
      const marked = String(properties[REAL] || '').toLowerCase() === 'true';
      const validRole = GIFT_ROLES.includes(properties[ROLE]);
      const legacy = String(properties[COMPANION] || '').toLowerCase() === 'true';
      const groupId = String(properties[GROUP] || '');
      if (item.key && (legacy || (marked && (!validRole || !groupId || !mains.has(groupId))))) {
        updates[item.key] = 0;
      }
    });
    return updates;
  };
  const sectionIds = (payload = {}, responseBody = {}) => {
    const ids = new Set();
    const requested = Array.isArray(payload.sections)
      ? payload.sections
      : typeof payload.sections === 'string'
        ? payload.sections.split(',')
        : [];
    requested.filter(Boolean).forEach((id) => ids.add(id));
    Object.keys(responseBody.sections || {}).forEach((id) => ids.add(id));
    document.querySelectorAll(
      'cart-items[data-id], #main-cart-footer[data-id]'
    ).forEach((node) => ids.add(node.dataset.sectionId || node.dataset.id));
    ids.add('cart-drawer');
    ids.add('cart-icon-bubble');
    return [...ids].filter(Boolean).slice(0, 5);
  };
  const realGiftSectionIds = () => [...document.querySelectorAll(
    '[data-safewalk-real-gifts-section][data-section-id]'
  )].map((node) => node.dataset.sectionId).filter(Boolean).slice(0, 5);
  const refreshRealGiftSections = async (responseBody = {}) => {
    const ids = realGiftSectionIds();
    if (!ids.length) return;
    const sections = { ...(responseBody.sections || {}) };
    const missing = ids.filter((id) => !sections[id]);
    if (missing.length) {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', missing.join(','));
      const response = await nativeFetch(url.href, {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`gift-sections-${response.status}`);
      Object.assign(sections, await response.json());
    }
    if (ids.some((id) => typeof sections[id] !== 'string' || !sections[id].trim())) {
      return;
    }
    window.SafeWalkV6RealGifts?.render?.({ sections });
  };
  const updateLines = async (updates, payload = {}, responseBody = {}) => {
    if (!Object.keys(updates).length) return { required: false, response: null };
    const body = { updates };
    const sections = sectionIds(payload, responseBody);
    if (sections.length) {
      body.sections = sections;
      body.sections_url = payload.sections_url || window.location.pathname;
    }
    const response = await nativeFetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    return { required: true, response: response.ok ? response : null };
  };
  const syncMainIndexes = (cart) => {
    window.SafeWalkV6RealGiftMainIndexes = (cart?.items || []).flatMap((item, index) => (
      propertiesFor(item)[ROLE] === 'main' && Number(item.quantity) === 1 ? [index + 1] : []
    ));
    window.SafeWalkV6RealGiftMainIndexesReady = true;
  };
  const inspectCart = (cart) => {
    syncMainIndexes(cart);
    const contract = window.SafeWalkV6RealGiftContract;
    const config = contract?.configFromRegistry?.();
    const staleGroups = window.SafeWalkV6RealGiftMigration?.staleGroups?.(cart, config) || [];
    window.SafeWalkV6LegacyGiftGroups = staleGroups;
    if (staleGroups.length) document.documentElement.dataset.safewalkGiftRecovery = 'readd';
    else delete document.documentElement.dataset.safewalkGiftRecovery;
    const classification = contract?.classifyCart?.(cart, config)
      || { groups: [], invalidGiftItems: [] };
    const state = classification.invalidGiftItems.length
      || classification.groups.some((group) => !group.complete)
      ? 'invalid'
      : 'ready';
    setState(state);
    return state;
  };
  const recoverLegacyCart = () => serialize(async () => {
    setState('pending');
    try {
      const response = await nativeFetch(cartUrl, {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error('legacy-cart');
      const cart = await response.json();
      const contract = window.SafeWalkV6RealGiftContract;
      const config = contract?.configFromRegistry?.();
      const migration = window.SafeWalkV6RealGiftMigration;
      const groups = migration?.staleGroups?.(cart, config) || [];
      const updates = migration?.removalUpdates?.(groups) || {};
      if (!groups.length || !Object.keys(updates).length) throw new Error('legacy-group');
      const readdUrl = migration.readdUrl(groups, config);
      if (!readdUrl) throw new Error('legacy-url');
      const classification = contract?.classifyCart?.(cart, config)
        || { groups: [], invalidGiftItems: [] };
      const staleIds = new Set(groups.map((group) => group.groupId));
      if (classification.invalidGiftItems.length
        || classification.groups.some((group) => !group.complete && !staleIds.has(group.groupId))) {
        throw new Error('legacy-cart-ambiguous');
      }
      const removal = await updateLines(updates, {}, cart);
      if (!removal.response) throw new Error('legacy-remove');
      let body = await removal.response.clone().json().catch(() => null);
      if (!body) {
        const verifyResponse = await nativeFetch(cartUrl, {
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        });
        body = verifyResponse.ok ? await verifyResponse.json().catch(() => null) : null;
      }
      if (!body || migration.staleGroups(body, config).length) throw new Error('legacy-verify');
      const removedKeys = new Set(Object.keys(updates));
      if ((body.items || []).some((item) => removedKeys.has(item.key))) throw new Error('legacy-remains');
      const state = inspectCart(body);
      window.SafeWalkV6RealGifts?.render?.(body);
      if (state !== 'ready') throw new Error('legacy-cart-invalid');
      refreshRealGiftSections(body).catch(() => {});
      return { ok: true, readdUrl };
    } catch (error) {
      setState('invalid');
      return {
        ok: false,
        reason: error instanceof Error ? error.message : 'legacy-error'
      };
    }
  });
  const wrappedFetch = async (input, init = {}) => {
    if (!isCartMutation(input)) return nativeFetch(input, init);
    pendingMutations += 1;
    setState('pending');
    return serialize(async () => {
      const payloadPromise = requestPayload(input, init);
      let response = null;
      try {
        response = await nativeFetch(input, init);
        if (!response.ok) {
          setState('invalid');
          return response;
        }
        const [payload, cart] = await Promise.all([payloadPromise, responseCart(response)]);
        if (!cart) {
          setState('invalid');
          return response;
        }
        const cleanup = await updateLines(cleanupUpdates(cart), payload || {}, cart);
        if (!cleanup.required) {
          inspectCart(cart);
          await refreshRealGiftSections(cart);
          return response;
        }
        if (!cleanup.response) {
          syncMainIndexes(cart);
          setState('invalid');
          return response;
        }
        const cleanedCart = await responseCart(cleanup.response);
        if (cleanedCart) {
          inspectCart(cleanedCart);
          await refreshRealGiftSections(cleanedCart);
        }
        else setState('invalid');
        return cleanup.response;
      } catch (error) {
        setState('invalid');
        if (response) return response;
        throw error;
      }
    }).finally(() => {
      pendingMutations = Math.max(0, pendingMutations - 1);
      publishState(pendingMutations > 0 ? 'pending' : deferredState);
    });
  };
  const reconcileExistingCart = () => serialize(async () => {
    setState('pending');
    try {
      const response = await nativeFetch(cartUrl, {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) {
        setState('invalid');
        return false;
      }
      const cart = await response.json();
      const cleanup = await updateLines(cleanupUpdates(cart), {}, cart);
      if (!cleanup.required) {
        inspectCart(cart);
        await refreshRealGiftSections(cart);
        return true;
      }
      if (!cleanup.response) {
        syncMainIndexes(cart);
        setState('invalid');
        return false;
      }
      const body = await cleanup.response.clone().json().catch(() => null);
      if (!body) {
        setState('invalid');
        return false;
      }
      inspectCart(body);
      window.SafeWalkV6RealGifts?.render?.(body);
      await refreshRealGiftSections(body);
      return true;
    } catch (error) {
      setState('invalid');
      return false;
    }
  });

  window.fetch = wrappedFetch;
  window.SafeWalkV6RealGiftLifecycle = Object.freeze({
    cleanupUpdates,
    reconcileExistingCart,
    recoverLegacyCart
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcileExistingCart, { once: true });
  } else {
    reconcileExistingCart();
  }
  window.addEventListener('pageshow', reconcileExistingCart);
  document.addEventListener('shopify:section:load', reconcileExistingCart);
})();
